'use client'

import { useState, useCallback } from 'react'
import { toast } from 'sonner'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Database, Link as LinkIcon } from 'lucide-react'
import { IntegrationCardLayout } from '@/components/integrations/integration-card-layout'
import { AddCrmIntegrationDialog } from './add-crm-integration-dialog'
import { UpdateCredentialsDialog } from './update-credentials-dialog'
import { CrmIntegrationItem } from './crm-integration-item'
import { useCrmIntegrations, type CrmIntegrationRecord } from '@/hooks/integrations/use-crm-integrations'
import type { CrmProviderType } from '@/lib/crm/types'
import { useTranslations } from "next-intl"


export function CrmIntegrationCard() {
  const tUi = useTranslations("Crm.CrmCrmIntegrationCard")
  const tCommonUi = useTranslations("Common")

  const {
    integrations,
    providers,
    error,
    isLoading,
    isConnecting,
    disconnectingId,
    togglingId,
    testingId,
    connectIntegration,
    prepareOAuthInstall,
    reconnectOAuth,
    reconnectingId,
    disconnectIntegration,
    toggleIntegration,
    testIntegration,
    updateCredentials,
    updatingCredentialsId,
    getProvider,
    clearError,
    refreshIntegrations,
  } = useCrmIntegrations()

  const [showAddDialog, setShowAddDialog] = useState(false)
  const [updatingIntegration, setUpdatingIntegration] = useState<CrmIntegrationRecord | null>(null)

  const handleAddClick = useCallback(() => {
    setShowAddDialog(true)
  }, [])

  const handleConnect = useCallback(
    async (
      provider: CrmProviderType,
      name: string,
      credentials: Record<string, unknown>,
      metadata?: Record<string, unknown>,
    ) => {
      return await connectIntegration(provider, name, credentials, metadata)
    },
    [connectIntegration],
  )

  const handleUpdateCredentials = useCallback((integration: CrmIntegrationRecord) => {
    clearError()
    setUpdatingIntegration(integration)
  }, [clearError])

  const handleReconnect = useCallback(
    async (integration: CrmIntegrationRecord) => {
      const result = await reconnectOAuth(integration.provider, integration.id)
      if (result.success && result.authorizeUrl) {
        // Full-page nav into Kommo's consent screen; the callback redirects back to this surface.
        window.location.assign(result.authorizeUrl)
      }
    },
    [reconnectOAuth],
  )

  const handleTest = useCallback(
    async (integrationId: string) => {
      const result = await testIntegration(integrationId)
      if (result.success) {
        toast.success(result.message || 'Connection successful')
        await refreshIntegrations()
      }
    },
    [testIntegration, refreshIntegrations],
  )

  const hasIntegrations = integrations.length > 0

  return (
    <>
      <IntegrationCardLayout
        title={"CRM"}
        description={tUi("connectYourCRMToSyncContactsAndConversations")}
        icon={<Database className="h-5 w-5" />}
      >
        <div className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>
                {error.error}
                {error.troubleshooting && error.troubleshooting.length > 0 && (
                  <ul className="list-disc list-inside mt-2 text-xs">
                    {error.troubleshooting.map((tip, i) => (
                      <li key={i}>{tip}</li>
                    ))}
                  </ul>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    clearError()
                    refreshIntegrations()
                  }}
                  className="mt-2"
                >
                  <span key={tCommonUi("retry")}>{tCommonUi("retry")}</span></Button>
              </AlertDescription>
            </Alert>
          )}

          {/* Connected Integrations */}
          {isLoading ? (
            <div className="animate-pulse space-y-2">
              <div className="h-16 bg-gray-200 rounded w-full"></div>
            </div>
          ) : hasIntegrations ? (
            <div className="space-y-2">
              {integrations.map((integration) => (
                <CrmIntegrationItem
                  key={integration.id}
                  integration={integration}
                  provider={getProvider(integration.provider)}
                  onTest={handleTest}
                  onToggle={toggleIntegration}
                  onDisconnect={disconnectIntegration}
                  onUpdateCredentials={handleUpdateCredentials}
                  onReconnect={handleReconnect}
                  isTesting={testingId === integration.id}
                  isToggling={togglingId === integration.id}
                  isDisconnecting={disconnectingId === integration.id}
                  isReconnecting={reconnectingId === integration.id}
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              <span key={tUi("noCRMIntegrationsConnectedYet")}>{tUi("noCRMIntegrationsConnectedYet")}</span></p>
          )}

          {/* Add Integration Button */}
          <div className="flex justify-start">
            <Button onClick={handleAddClick}>
              <LinkIcon aria-hidden="true" className="mr-2 h-4 w-4" />
              <span><span key={tUi("connectCRM")}>{tUi("connectCRM")}</span></span>
            </Button>
          </div>
        </div>
      </IntegrationCardLayout>

      <AddCrmIntegrationDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        providers={providers}
        onConnect={handleConnect}
        onPrepareOAuth={prepareOAuthInstall}
        isConnecting={isConnecting}
        error={error}
        onClearError={clearError}
      />

      <UpdateCredentialsDialog
        open={!!updatingIntegration}
        onOpenChange={(open) => { if (!open) setUpdatingIntegration(null) }}
        integration={updatingIntegration}
        provider={updatingIntegration ? getProvider(updatingIntegration.provider) : undefined}
        onUpdate={updateCredentials}
        isUpdating={updatingCredentialsId === updatingIntegration?.id}
        error={error}
        onClearError={clearError}
      />
    </>
  )
}
